import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../App';
import { NavBar } from '../components/NavBar';
import { useSound } from '../hooks/useSound';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';
import { CurriculumMapButton } from '../components/CurriculumMap';
import axios from 'axios';

export function TopicDetail() {
  const { topic } = useParams();
  const [topicInfo, setTopicInfo] = useState(null);
  const navigate = useNavigate();
  const { playSound } = useSound();
  const { t } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/topics/${topic}/games`).then(r => {
      const totalItems = r.data.reduce((sum, g) => sum + g.count, 0);
      // Get difficulty counts
      const diffCounts = {};
      Promise.all(
        ['introductory', 'practice', 'mastery'].map(d =>
          axios.get(`${API}/topics/${topic}/games`, { params: { difficulty: d } })
            .then(resp => {
              diffCounts[d] = resp.data.reduce((sum, g) => sum + g.count, 0);
            })
        )
      ).then(() => {
        setTopicInfo({ totalItems, diffCounts, games: r.data });
      });
    }).catch(console.error);
  }, [topic]);

  const label = t(`topic.${topic}`) || topic;

  const DIFF_STYLES = {
    introductory: { bg: '#2F9E44', border: '#237B34' },
    practice: { bg: '#1971C2', border: '#1864AB' },
    mastery: { bg: '#E8590C', border: '#D9480F' },
  };

  return (
    <div data-testid="topic-detail-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <NavBar
        items={[label]}
        onBack={() => navigate('/browse')}
      />
      <div style={{ flex: 1, padding: '1.5rem 2rem', overflow: 'auto' }}>
        <h2 style={{
          fontFamily: 'Fredoka, sans-serif', fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
          fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: '0.5rem',
        }}>
          {label}
        </h2>
        {topicInfo && (
          <>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              {t('diff.chooseDifficulty')} {topicInfo.totalItems.toLocaleString()} {t('page.items')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <CurriculumMapButton
                queryParams={{ mode: 'topic', topic }}
                title={`${label} — ${t('page.curriculumAlignment')}`}
              />
            </div>
          </>
        )}

        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          {/* "All Difficulties" card */}
          <button
            data-testid="topic-all-difficulties-button"
            onClick={() => {
              playSound('click');
              navigate(`/games?mode=topic&topic=${topic}`);
            }}
            style={{
              width: '100%', padding: '1.25rem 1.5rem', marginBottom: '1.25rem',
              background: 'var(--primary)', color: 'white', border: 'none',
              borderRadius: '1.25rem', borderBottom: '6px solid #3B5BDB',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontFamily: 'Fredoka, sans-serif', fontSize: '1.3rem', fontWeight: 700,
              boxShadow: '0 4px 16px rgba(76,110,245,0.25)',
            }}
          >
            <span>{t('diff.allDifficulties')} {topicInfo ? ` — ${topicInfo.totalItems.toLocaleString()} ${t('page.items')}` : ''}</span>
            <ArrowRight size={24} />
          </button>

          {/* Difficulty cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {['introductory', 'practice', 'mastery'].map(d => {
              const s = DIFF_STYLES[d];
              const count = topicInfo?.diffCounts?.[d] || 0;
              // Hide sparse cells (1-7 items): teachers can still use "All Difficulties" which aggregates.
              // Empty cells (count === 0) are also hidden.
              const MIN_DIFFICULTY_THRESHOLD = 8;
              if (count > 0 && count < MIN_DIFFICULTY_THRESHOLD) return null;
              return (
                <button key={d} data-testid={`topic-diff-${d}-button`}
                  onClick={() => {
                    if (count === 0) return;
                    playSound('click');
                    navigate(`/games?mode=topic&topic=${topic}&difficulty=${d}`);
                  }}
                  disabled={count === 0}
                  style={{
                    width: '100%', padding: '1.25rem 1.5rem',
                    background: count > 0 ? 'var(--surface)' : 'var(--surface-el)',
                    border: `3px solid ${count > 0 ? s.bg + '40' : 'var(--border)'}`,
                    borderRadius: '1.25rem', borderLeft: `8px solid ${count > 0 ? s.bg : 'var(--border)'}`,
                    cursor: count > 0 ? 'pointer' : 'default',
                    textAlign: 'left', transition: 'all 0.15s',
                    opacity: count > 0 ? 1 : 0.5,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.2rem', fontWeight: 700, color: count > 0 ? s.bg : 'var(--text-muted)' }}>
                        {t(`diff.${d}`)}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                        {t(`diff.${d}Desc`)}
                      </div>
                    </div>
                    <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                      {count} {t('page.items')}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
